import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../Layout/Layout";
import { createOpsClient, type Feed, type Quote, type Rig, type Trip } from "../../lib/bookingApi";
import { formatLong } from "../../lib/dates";
import { loginRequest, pca, pcaReady } from "../../lib/msal";
import TripBoard from "./TripBoard";

type Tab = "quotes" | "trips" | "feeds";

const OpsApp = () => {
  const { instance, accounts } = useMsal();
  const signedIn = useIsAuthenticated();
  const [rig, setRig] = useState<Rig>("commodore");
  const [tab, setTab] = useState<Tab>("quotes");
  const [trips, setTrips] = useState<Trip[]>([]);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [feedName, setFeedName] = useState("");
  const [feedUrl, setFeedUrl] = useState("");

  useEffect(() => {
    document
      .querySelector("meta[name='robots']")
      ?.setAttribute("content", "noindex, nofollow");
    if (!document.querySelector("meta[name='robots']")) {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex, nofollow";
      document.head.appendChild(meta);
    }
  }, []);

  const getToken = useCallback(async () => {
    const account = accounts[0] ?? instance.getAllAccounts()[0];
    if (!account) {
      throw new Error("Sign in required");
    }
    try {
      const silent = await instance.acquireTokenSilent({ ...loginRequest, account });
      return silent.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
        return new Promise<string>(() => undefined);
      }
      throw error;
    }
  }, [accounts, instance]);

  const client = useMemo(() => createOpsClient(getToken), [getToken]);
  const closedCount = quotes.filter((quote) => quote.status === "closed").length;
  const visibleQuotes = useMemo(
    () => (includeClosed ? quotes : quotes.filter((quote) => quote.status !== "closed")),
    [quotes, includeClosed],
  );

  const refresh = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const [nextTrips, nextFeeds, nextQuotes] = await Promise.all([
        client.listTrips(rig),
        client.listFeeds(rig),
        client.listQuotes(rig),
      ]);
      setTrips(nextTrips);
      setFeeds(nextFeeds);
      setQuotes(nextQuotes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load ops data");
    } finally {
      setBusy(false);
    }
  }, [client, rig]);

  useEffect(() => {
    if (signedIn) {
      void refresh();
    }
  }, [signedIn, refresh]);

  if (!signedIn) {
    return (
      <Layout title="Ops | Earthbound Martian">
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <h1 className="text-4xl font-bold mb-4">Ops</h1>
          <p className="text-zinc-600 mb-8">Sign in with your Earthbound Martian Microsoft account.</p>
          <button
            className="bg-mars hover:bg-sunset text-white px-8 py-4 rounded-2xl font-semibold"
            onClick={() => instance.loginRedirect(loginRequest)}
          >
            Sign in
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Ops | Earthbound Martian">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold">Ops</h1>
            <p className="text-zinc-500">{accounts[0]?.name}</p>
          </div>
          <button
            className="text-sm underline"
            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}/ops` })}
          >
            Sign out
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {(["commodore", "serenity"] as Rig[]).map((item) => (
            <button
              key={item}
              className={`px-4 py-2 rounded-2xl font-semibold ${rig === item ? "bg-mars text-white" : "bg-white border"}`}
              onClick={() => setRig(item)}
            >
              {item[0].toUpperCase() + item.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex gap-3 mb-8">
          {([
            ["quotes", "Quotes"],
            ["trips", "Calendar"],
            ["feeds", "Feeds"],
          ] as [Tab, string][]).map(([id, label]) => (
            <button
              key={id}
              className={`px-4 py-2 rounded-2xl ${tab === id ? "bg-alt-blk text-white" : "bg-zinc-100"}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {error && <p className="text-mars mb-4">{error}</p>}
        {busy && <p className="text-zinc-500 mb-4">Refreshing…</p>}

        {tab === "quotes" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={(event) => setIncludeClosed(event.target.checked)}
                />
                Include closed
              </label>
              {!includeClosed && closedCount > 0 && (
                <span className="text-sm text-zinc-500">
                  {closedCount} closed hidden
                </span>
              )}
            </div>
            {quotes.length === 0 && <p className="text-zinc-500">No quote requests yet.</p>}
            {quotes.length > 0 && visibleQuotes.length === 0 && (
              <p className="text-zinc-500">No open quotes. Include closed to see the archive.</p>
            )}
            {visibleQuotes.map((quote) => (
              <div
                key={quote.id}
                className={`bg-white rounded-2xl p-5 shadow-sm border ${quote.status === "closed" ? "opacity-60" : ""}`}
              >
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <div className="font-semibold text-lg">{quote.name}</div>
                    <div className="text-zinc-600 text-sm">
                      {quote.email}
                      {quote.phone ? ` · ${quote.phone}` : ""}
                    </div>
                    <div className="text-zinc-600 text-sm mt-1">
                      {formatLong(quote.startDate)} → {formatLong(quote.endDate)}
                      {quote.destination ? ` · ${quote.destination}` : ""}
                      {quote.guests ? ` · ${quote.guests} guests` : ""}
                    </div>
                    {quote.notes && <p className="text-zinc-600 mt-2">{quote.notes}</p>}
                  </div>
                  <select
                    value={quote.status}
                    className="border rounded-xl px-3 py-2 h-fit"
                    onChange={(event) => {
                      void client
                        .updateQuote(rig, quote.id, { status: event.target.value as Quote["status"] })
                        .then(refresh);
                    }}
                  >
                    {["new", "contacted", "quoted", "booked", "closed"].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "trips" && (
          <TripBoard
            trips={trips}
            onCreate={async (input) => {
              try {
                await client.createTrip(rig, input);
                await refresh();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Could not save trip");
                throw err;
              }
            }}
            onDelete={async (trip) => {
              try {
                await client.deleteTrip(rig, trip.id);
                await refresh();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Could not delete trip");
              }
            }}
          />
        )}

        {tab === "feeds" && (
          <div className="space-y-6">
            <div className="bg-zinc-50 rounded-2xl p-5">
              <div className="font-medium mb-1">Our export URL</div>
              <code className="text-sm break-all">{client.exportUrl(rig)}</code>
              <p className="text-sm text-zinc-500 mt-2">
                Subscribe the platforms to this feed. It only includes trips you add here, not
                dates we imported.
              </p>
            </div>
            <form
              className="bg-white rounded-2xl p-6 border space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                void client
                  .createFeed(rig, { name: feedName, url: feedUrl })
                  .then(() => {
                    setFeedName("");
                    setFeedUrl("");
                    return refresh();
                  })
                  .catch((err: Error) => setError(err.message));
              }}
            >
              <h2 className="font-semibold text-xl">Add a platform calendar</h2>
              <p className="text-sm text-zinc-500">
                Paste the listing’s <strong>iCal export</strong> URL (usually ends in{" "}
                <code>.ics</code>). Outdoorsy and RVEzy only publish upcoming trips — we keep
                past imports after they fall off the feed.
              </p>
              <input
                required
                placeholder="RVShare"
                value={feedName}
                onChange={(e) => setFeedName(e.target.value)}
                className="w-full border rounded-xl px-3 py-2"
              />
              <input
                required
                placeholder="https://…"
                value={feedUrl}
                onChange={(e) => setFeedUrl(e.target.value)}
                className="w-full border rounded-xl px-3 py-2"
              />
              <button className="bg-mars text-white px-5 py-3 rounded-2xl font-semibold" type="submit">
                Add feed
              </button>
            </form>
            {feeds.map((feed) => (
              <div key={feed.id} className="bg-white rounded-2xl p-5 border">
                <div className="font-semibold">{feed.name}</div>
                <div className="text-sm text-zinc-500 break-all">{feed.url}</div>
                <div className="text-sm mt-2">
                  {feed.lastSyncAt ? `Last sync ${new Date(feed.lastSyncAt).toLocaleString()}` : "Not synced yet"}
                  {feed.lastEventCount != null ? ` · ${feed.lastEventCount} events` : ""}
                </div>
                {feed.lastError && (
                  <p
                    className={`text-sm mt-1 ${feed.lastError.includes("no upcoming") ? "text-zinc-600" : "text-mars"}`}
                  >
                    {feed.lastError}
                  </p>
                )}
                <div className="flex gap-4 mt-3">
                  <button
                    className="underline text-sm"
                    onClick={() => void client.syncFeed(rig, feed.id).then(refresh)}
                  >
                    Sync now
                  </button>
                  <button
                    className="underline text-sm text-mars"
                    onClick={() => void client.deleteFeed(rig, feed.id).then(refresh)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

const Ops = () => {
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState("");

  useEffect(() => {
    pcaReady
      .then(() => setReady(true))
      .catch((error: unknown) =>
        setBootError(error instanceof Error ? error.message : "Could not start sign-in"),
      );
  }, []);

  if (bootError) {
    return (
      <Layout title="Ops | Earthbound Martian">
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <h1 className="text-4xl font-bold mb-4">Ops</h1>
          <p className="text-mars">{bootError}</p>
        </div>
      </Layout>
    );
  }

  if (!ready) {
    return (
      <Layout title="Ops | Earthbound Martian">
        <div className="max-w-5xl mx-auto px-6 py-20">Signing you in…</div>
      </Layout>
    );
  }

  return (
    <MsalProvider instance={pca}>
      <OpsApp />
    </MsalProvider>
  );
};

export default Ops;
